
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Crown, CreditCard, Clock, CheckCircle, AlertTriangle } from 'lucide-react';
import { usePEOEnterpriseFeatures } from '@/hooks/peo/usePEOEnterpriseFeatures';
import type { PEOProvider } from '@/types/database/peo-types';

interface SubscriptionManagementProps {
  providerInfo: PEOProvider | null;
}

export const SubscriptionManagement = ({ providerInfo }: SubscriptionManagementProps) => {
  const { 
    subscriptionTier, 
    isSubscriptionActive, 
    isTrialing, 
    trialEndsAt,
    upgradeRequired 
  } = usePEOEnterpriseFeatures(providerInfo);

  if (!providerInfo) {
    return null;
  }

  const getStatusIcon = () => {
    if (isSubscriptionActive) return <CheckCircle className="h-5 w-5 text-green-500" />;
    if (isTrialing) return <Clock className="h-5 w-5 text-amber-500" />;
    return <AlertTriangle className="h-5 w-5 text-red-500" />;
  };

  const getStatusText = () => {
    if (isSubscriptionActive) return 'Active';
    if (isTrialing) return 'Trial';
    return 'Basic';
  };

  const getTrialProgress = () => {
    if (!isTrialing || !trialEndsAt) return 0;
    
    const now = new Date();
    // 2 months = 60 days
    const trialStart = new Date(trialEndsAt.getTime() - (60 * 24 * 60 * 60 * 1000));
    const totalDuration = trialEndsAt.getTime() - trialStart.getTime();
    const elapsed = now.getTime() - trialStart.getTime();
    
    return Math.max(0, Math.min(100, (elapsed / totalDuration) * 100));
  };

  const getDaysRemaining = () => {
    if (!isTrialing || !trialEndsAt) return 0;
    
    const now = new Date();
    const diffTime = trialEndsAt.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diffTime / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="space-y-6">
      {/* Current Plan */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Crown className="h-5 w-5" />
            <span>Current Plan</span>
          </CardTitle>
          <CardDescription>
            Manage your PEO enterprise subscription
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {getStatusIcon()}
                <div>
                  <h3 className="font-semibold capitalize">{subscriptionTier}</h3>
                  <p className="text-sm text-muted-foreground">{getStatusText()}</p>
                </div>
              </div>
              <Badge variant={isSubscriptionActive ? 'default' : isTrialing ? 'secondary' : 'outline'}>
                {subscriptionTier === 'enterprise' ? '$2,000/month' : 'Free'}
              </Badge>
            </div>

            {/* Trial Progress */}
            {isTrialing && trialEndsAt && (
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Trial Progress (2 months free)</span>
                  <span>{getDaysRemaining()} days remaining</span>
                </div>
                <Progress value={getTrialProgress()} className="h-2" />
                <p className="text-xs text-muted-foreground">
                  Trial ends on {trialEndsAt.toLocaleDateString()}
                </p>
              </div>
            )}

            {/* Enterprise Features Summary */}
            {(isSubscriptionActive || isTrialing) && (
              <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
                <h4 className="font-medium text-amber-800 mb-2">Enterprise Features Included:</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>✓ Unlimited shopper linking and management</li>
                  <li>✓ Advanced analytics and reporting</li>
                  <li>✓ Custom application workflows</li>
                  <li>✓ White-label branding options</li>
                  <li>✓ Priority support (24/7)</li>
                </ul>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Upgrade/Manage Actions */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <CreditCard className="h-5 w-5" />
            <span>Billing & Subscription</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {upgradeRequired && (
              <div className="text-center space-y-4">
                <div className="p-6 border-2 border-dashed border-amber-200 rounded-lg bg-gradient-to-br from-amber-50 to-yellow-50">
                  <Crown className="h-12 w-12 text-amber-500 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold text-amber-800 mb-2">
                    Unlock Enterprise Features
                  </h3>
                  <p className="text-amber-700 mb-2">
                    Get access to user management, advanced analytics, and priority support
                  </p>
                  <p className="text-sm text-amber-600 mb-4">
                    <strong>2 months FREE trial</strong>, then $2,000/month
                  </p>
                  <Button size="lg" onClick={() => handleStartTrial()}>
                    <Clock className="mr-2 h-4 w-4" />
                    Start 2-Month Free Trial
                  </Button>
                </div>
              </div>
            )}

            {isTrialing && (
              <div className="text-center space-y-4">
                <div className="p-6 border border-amber-200 rounded-lg bg-amber-50">
                  <Clock className="h-8 w-8 text-amber-500 mx-auto mb-2" />
                  <h3 className="font-semibold text-amber-800">Trial Active</h3>
                  <p className="text-amber-700 text-sm mb-2">
                    {getDaysRemaining()} days remaining in your 2-month enterprise trial
                  </p>
                  <p className="text-xs text-amber-600 mb-4">
                    Subscription will automatically begin at $2,000/month after trial
                  </p>
                  <Button onClick={() => handleUpgrade()}>
                    <Crown className="mr-2 h-4 w-4" />
                    Subscribe Now - $2,000/month
                  </Button>
                </div>
              </div>
            )}

            {isSubscriptionActive && (
              <div className="space-y-3">
                <Button variant="outline" className="w-full">
                  <CreditCard className="mr-2 h-4 w-4" />
                  Manage Billing
                </Button>
                <Button variant="outline" className="w-full">
                  View Invoice History
                </Button>
                <Button variant="outline" className="w-full text-red-600 hover:text-red-700">
                  Cancel Subscription
                </Button>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

function handleStartTrial() {
  // TODO: Implement trial activation
  console.log('Starting 2-month enterprise trial');
}

function handleUpgrade() {
  // TODO: Implement Stripe checkout redirect for $2,000/month PEO plan
  console.log('Redirecting to Stripe checkout for PEO enterprise upgrade - $2,000/month');
}
